import { ArrowRightOutlined } from '@ant-design/icons';
import { Card, Divider, Flex, Typography } from 'antd';
import { Fragment } from 'react';
import { PhotoProvider, PhotoView } from 'react-photo-view';

import Accordion from 'components/Accordion';
import CustomLoader from 'components/CustomLoader';
import Tag from 'components/Tags';
import TaskInfo from 'components/TaskInfo';

import StatusTag from 'components/StatusTag';
import { getStatusTagColor } from 'screens/farmer/helper';
import { Document, Farmer } from 'types/common';
import DateTime from 'utils/datetime';
import {
  getGoogleMapURL,
  renderDate,
  renderInfoValue,
  validateImageURL,
} from 'utils/helpers';
import { renderContractStatus } from '../farmer-table/FarmersTableHeader';

interface TFarmer extends Farmer {
  role?: string;
}

interface FarmerQueryData extends TFarmer {
  signedContractImage?: Document;
  farmerWithContractImage?: Document;
  nationalIdImages?: string[];
}

interface PersonalDetailsTabProps {
  data?: TFarmer;
  farmerInfo?: FarmerQueryData;
  isLoadingDocuments: boolean;
  prefetchFarmer: (farmerId: string) => void;
  farmersSharedPhoneNumber?: TFarmer[];
  showFGLData: boolean;
  setShowFGLData: (show: boolean) => void;
}

const GROUPLEADER = 'Group Leader';

const PersonalDetailsTab = ({
  data,
  farmerInfo,
  isLoadingDocuments,
  prefetchFarmer,
  farmersSharedPhoneNumber,
  showFGLData,
  setShowFGLData,
}: PersonalDetailsTabProps) => {
  const renderPhoneVerification = (status?: boolean) => {
    const verificationStatus = status ? 'verified' : 'unverified';
    return <StatusTag status={verificationStatus} />;
  };

  const renderFarmerGroupLeader = (data?: TFarmer) => {
    const isLinkVisible = !(data?.role === GROUPLEADER || showFGLData);

    const handleFGLClick = () => {
      if (isLinkVisible && !!data?.cube?.groupLeader?.name?.length) {
        setShowFGLData(true);
      }
    };
    return (
      <div
        className={`${isLinkVisible && !!data?.cube?.groupLeader?.name?.length ? 'fgl-link-text' : ''}`}
        onClick={handleFGLClick}
        onMouseOver={() =>
          isLinkVisible && prefetchFarmer(data?.cube?.groupLeader?.id ?? '')
        }
      >
        {renderInfoValue(data?.cube?.groupLeader?.name, 'N/A')}{' '}
        {isLinkVisible && !!data?.cube?.groupLeader?.name?.length && (
          <ArrowRightOutlined />
        )}
      </div>
    );
  };

  const renderLocation = (location = '') => {
    const y = location?.split(',')[0];
    const x = location?.split(',')[1];

    if (
      !x ||
      !y ||
      x === 'undefined' ||
      y === 'undefined' ||
      x === 'null' ||
      y === 'null'
    ) {
      return <p>N/A</p>;
    }

    return (
      <a
        className="link-text"
        href={getGoogleMapURL(x, y)}
        target="_blank"
        rel="noreferrer"
      >
        {Number(y)?.toFixed(2)}, {Number(x)?.toFixed(2)}
      </a>
    );
  };

  const renderImage = (image: string, alt = '') => {
    if (!image || !validateImageURL(image)) return 'N/A';

    return (
      <PhotoView key={image} src={image}>
        <img src={image} alt={alt} className="img-fluid me-2" />
      </PhotoView>
    );
  };

  const renderNationalIdImages = (nationalIdImages: string[] = []) => {
    if (isLoadingDocuments) return <CustomLoader size={'extra-small'} />;
    if (!nationalIdImages.length) return <p>N/A</p>;
    return (
      <div className="d-flex mt-1">
        <PhotoProvider>
          {nationalIdImages.map((image) => renderImage(image, 'National ID'))}
        </PhotoProvider>
      </div>
    );
  };

  const renderContractImage = (image: Document | undefined, alt: string) => {
    if (isLoadingDocuments) return <CustomLoader size={'extra-small'} />;
    if (!image?.url) return 'N/A';
    return (
      <Flex vertical gap={6} className="mt-1">
        <PhotoProvider>{renderImage(image.url, alt)}</PhotoProvider>
        {image.createdDate && (
          <Typography.Text className="info-label" type="secondary">
            Date taken:{' '}
            {renderInfoValue(
              DateTime.format(image.createdDate, 'DD/MM/YYYY, HH:mm'),
            )}
          </Typography.Text>
        )}
      </Flex>
    );
  };

  const {
    signedContractImage,
    farmerWithContractImage,
    nationalIdImages = [],
  } = farmerInfo || {};

  return (
    <Card>
      <Flex wrap="wrap" style={{ rowGap: 10 }}>
        <TaskInfo
          header={'Role'}
          renderValue={() => renderInfoValue(data?.role, 'N/A')}
        />
        <TaskInfo
          header={'Farmer Group Leader'}
          renderValue={() => renderFarmerGroupLeader(data)}
        />
        <TaskInfo
          header={'Location'}
          renderValue={() => renderLocation(data?.location)}
        />
        <TaskInfo
          header={'Total Hectares'}
          renderValue={() => renderInfoValue(data?.area, 'N/A')}
        />
        <TaskInfo
          header={'Date Created'}
          renderValue={() => renderDate(data?.createdAt, 'N/A')}
        />
        <TaskInfo
          header={'Farmer ID'}
          renderValue={() => renderInfoValue(data?.id, 'N/A')}
        />
        <TaskInfo
          header={'Status'}
          renderValue={() => (
            <div>
              {data?.status ? (
                <Tag
                  label={data.status}
                  color={getStatusTagColor(data.status)}
                />
              ) : (
                'N/A'
              )}
            </div>
          )}
        />
      </Flex>
      <Divider />
      <Flex vertical gap={15}>
        <TaskInfo
          header="Contract Status"
          renderValue={() =>
            renderContractStatus(data?.contractStatus) || 'N/A'
          }
        />
        <TaskInfo
          header="Signature Contract Picture"
          renderValue={() =>
            renderContractImage(signedContractImage, 'Signed Contract')
          }
        />
        <TaskInfo
          header="Farmer with Contract Picture"
          renderValue={() =>
            renderContractImage(farmerWithContractImage, 'Farmer with Contract')
          }
        />
      </Flex>
      <Divider />
      <Flex wrap="wrap" style={{ rowGap: 10 }}>
        <TaskInfo
          header={'Phone Number'}
          renderValue={() =>
            renderInfoValue(data?.phoneNumber ?? 'No Phone Number')
          }
        />
        <TaskInfo
          header={'Verification Status'}
          renderValue={() => renderPhoneVerification(data?.otpVerified)}
        />
        <TaskInfo
          header={'Verification Channel'}
          value={
            data?.otpVerified
              ? data?.otpChannel === 'WA'
                ? 'Whatsapp'
                : data?.otpChannel
              : 'N/A'
          }
        />
        {farmersSharedPhoneNumber?.length ? (
          <Accordion title="Shared Phone Number" className="w-100 mt-4">
            <Flex wrap="wrap" style={{ rowGap: 10 }}>
              {farmersSharedPhoneNumber.map((farmer) => (
                <Fragment key={farmer?.id || Math.random().toString()}>
                  <TaskInfo
                    header="Farmer Name"
                    renderValue={() => renderInfoValue(farmer?.name, 'N/A')}
                  />
                  <TaskInfo
                    header="Farmer Group Leader"
                    renderValue={() =>
                      renderInfoValue(farmer?.leaderName, 'N/A')
                    }
                  />
                </Fragment>
              ))}
            </Flex>
          </Accordion>
        ) : null}
      </Flex>
      <Divider />
      <Flex vertical gap={10}>
        <TaskInfo
          header="National ID Number"
          renderValue={() => renderInfoValue(data?.nationalIdNumber, 'N/A')}
        />
        <TaskInfo
          header="National ID Picture"
          renderValue={() => renderNationalIdImages(nationalIdImages)}
        />
      </Flex>
      <Divider />
      <Flex className="info-container">
        <TaskInfo
          header="Agronomist"
          renderValue={() =>
            renderInfoValue(
              data?.cube?.rizeusersByFieldCoordinator?.name,
              'N/A',
            )
          }
        />
      </Flex>
    </Card>
  );
};

export default PersonalDetailsTab;
